import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { caseKeys } from "@/domains/cases/api/keys";
import type {
  CaseFile,
  CaseHistoryEntry,
  CaseNote,
  CaseStatus,
} from "@/domains/cases/api/types";
import {
  addAssetToCase,
  addCaseNote,
  appendHistory,
  removeAssetFromCase,
  updateCaseStatus,
} from "@/domains/cases/mocks/casesService";
import { assetKeys } from "@/domains/assets";

type AssetVars = {
  caseId: string;
  assetId: string;
  actor: { id: string; name: string };
};

export function useAddAssetToCase(): UseMutationResult<CaseFile, Error, AssetVars> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ caseId, assetId, actor }: AssetVars) =>
      addAssetToCase(caseId, assetId, actor),
    onSuccess: (file) => {
      client.invalidateQueries({ queryKey: caseKeys.detail(file.id) });
      client.invalidateQueries({ queryKey: caseKeys.lists() });
      client.invalidateQueries({ queryKey: assetKeys.all });
    },
  });
}

export function useRemoveAssetFromCase(): UseMutationResult<CaseFile, Error, AssetVars> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ caseId, assetId, actor }: AssetVars) =>
      removeAssetFromCase(caseId, assetId, actor),
    onSuccess: (file) => {
      client.invalidateQueries({ queryKey: caseKeys.detail(file.id) });
      client.invalidateQueries({ queryKey: caseKeys.lists() });
      client.invalidateQueries({ queryKey: assetKeys.all });
    },
  });
}

type AddNoteVars = {
  caseId: string;
  note: Omit<CaseNote, "id" | "createdAt">;
};

export function useAddCaseNote(): UseMutationResult<CaseFile, Error, AddNoteVars> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ caseId, note }: AddNoteVars) => addCaseNote(caseId, note),
    onSuccess: (file) => {
      client.invalidateQueries({ queryKey: caseKeys.detail(file.id) });
    },
  });
}

type StatusVars = {
  caseId: string;
  status: CaseStatus;
  actor: { id: string; name: string };
};

export function useUpdateCaseStatus(): UseMutationResult<CaseFile, Error, StatusVars> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ caseId, status, actor }: StatusVars) =>
      updateCaseStatus(caseId, status, actor),
    onSuccess: (file) => {
      client.invalidateQueries({ queryKey: caseKeys.detail(file.id) });
      client.invalidateQueries({ queryKey: caseKeys.lists() });
    },
  });
}

type HistoryVars = {
  caseId: string;
  entry: Omit<CaseHistoryEntry, "id" | "timestamp">;
};

export function useAppendCaseHistory(): UseMutationResult<CaseFile, Error, HistoryVars> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ caseId, entry }: HistoryVars) => appendHistory(caseId, entry),
    onSuccess: (file) => {
      client.invalidateQueries({ queryKey: caseKeys.detail(file.id) });
    },
  });
}
